import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { UsersThree, Plus, Key, PencilSimple, X, Prohibit, CheckCircle, CaretDown, CaretUp, ShieldCheck } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api, { apiErrorMessage } from "../lib/api";
import { PERMISSION_LABELS } from "../lib/format";

const emptyForm = {
  username: "", password: "", name: "", role: "staff",
  salesman_name: "", current_floor: "", core_floor: "", active: true, permissions: null,
  floor_target_scope: null, incentive_scope: null,
};

function Modal({ children, onClose, title, testId }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid={testId}>
      <div className="mt-8 w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="modal-close"><X size={20} /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

export default function Users() {
  const [users, setUsers] = useState(null);
  const [meta, setMeta] = useState(null);
  const [floors, setFloors] = useState([]);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [configFloors, setConfigFloors] = useState([]);
  const [showOverview, setShowOverview] = useState(false);

  const load = useCallback(async () => {
    const [u, m, f, t] = await Promise.all([
      api.get("/users"), api.get("/meta/permissions"), api.get("/floors"),
      api.get("/settings/targets").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data); setMeta(m.data); setFloors(f.data);
    const active = (t.data || []).filter((x) => x.active).map((x) => x.floor_name);
    setConfigFloors(active.length ? active : f.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm }); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.username, password: "", name: u.name || "", role: u.role,
      salesman_name: u.salesman_name || "", current_floor: u.current_floor || "",
      core_floor: u.core_floor || "",
      active: u.active, permissions: u.permissions_custom ? u.permissions : null,
      customPerms: false,
      floor_target_scope: u.floor_target_scope ?? null,
      incentive_scope: u.incentive_scope ?? null,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, role: form.role,
        salesman_name: form.salesman_name || null,
        current_floor: form.current_floor || null,
        core_floor: form.core_floor || null,
        active: form.active,
        permissions: form.customPerms ? (form.permissions || []) : null,
        floor_target_scope: form.floor_target_scope ?? null,
        incentive_scope: form.incentive_scope ?? null,
      };
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
        toast.success("User updated");
      } else {
        if (!form.username || !form.password) { toast.error("Username and password are required"); setSaving(false); return; }
        await api.post("/users", { ...payload, username: form.username, password: form.password });
        toast.success("User created");
      }
      setForm(null); setEditing(null);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    if (!pw) { toast.error("Enter a new password"); return; }
    try {
      await api.post(`/users/${pwUser.id}/password`, { password: pw });
      toast.success("Password updated");
      setPwUser(null); setPw("");
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail) || "Failed");
    }
  };

  const deactivate = async () => {
    setStatusBusy(true);
    try {
      await api.post(`/users/${confirmUser.id}/deactivate`);
      toast.success(`${confirmUser.username} deactivated`);
      setConfirmUser(null);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail) || "Failed to deactivate");
    } finally {
      setStatusBusy(false);
    }
  };

  const activate = async (u) => {
    try {
      await api.post(`/users/${u.id}/activate`);
      toast.success(`${u.username} activated`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail) || "Failed to activate");
    }
  };

  const togglePerm = (p) => {
    setForm((f) => {
      const cur = new Set(f.permissions || []);
      cur.has(p) ? cur.delete(p) : cur.add(p);
      return { ...f, permissions: [...cur] };
    });
  };

  const dashBase = () => (form.customPerms ? (form.permissions || []) : (editing ? editing.permissions : meta?.role_defaults?.[form.role]) || []);
  const dashHas = (p) => dashBase().includes(p);
  const toggleDash = (p) => {
    setForm((f) => {
      const base = new Set(dashBase());
      base.has(p) ? base.delete(p) : base.add(p);
      return { ...f, customPerms: true, permissions: [...base] };
    });
  };


  if (users === null) {
    return <Layout title="Users"><div className="h-64 animate-pulse rounded-lg border border-slate-200 bg-white" /></Layout>;
  }

  return (
    <Layout title="Users">
      <div className="animate-fade-in space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">{users.length} users</p>
          <button onClick={openCreate} data-testid="add-user-btn" className="flex items-center gap-1.5 rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800">
            <Plus size={16} weight="bold" /> Add User
          </button>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white" data-testid="access-overview">
          <button onClick={() => setShowOverview((v) => !v)} data-testid="access-overview-toggle"
            className="flex w-full items-center justify-between px-4 py-3 text-left">
            <span className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <ShieldCheck size={18} weight="bold" className="text-blue-700" /> Access Overview
              <span className="text-xs font-normal text-slate-400">· who can see what</span>
            </span>
            {showOverview ? <CaretUp size={16} weight="bold" className="text-slate-400" /> : <CaretDown size={16} weight="bold" className="text-slate-400" />}
          </button>
          {showOverview && (
            <div className="thin-scroll overflow-x-auto border-t border-slate-100">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="px-3 py-2.5">User</th>
                    <th className="px-3 py-2.5">Role</th>
                    <th className="px-3 py-2.5">Store ABV/ABQ</th>
                    <th className="px-3 py-2.5">Floor Target Access</th>
                    <th className="px-3 py-2.5">Incentive Access</th>
                    <th className="px-3 py-2.5 text-right">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`overview-row-${u.username}`} className="border-t border-slate-100 text-sm">
                      <td className="px-3 py-2.5 font-medium text-slate-800">{u.name} <span className="text-xs text-slate-400">@{u.username}</span></td>
                      <td className="px-3 py-2.5 capitalize text-slate-600">{u.role}</td>
                      <td className="px-3 py-2.5"><ScopePill on={u.role === "admin" || (u.permissions || []).includes("view_store_metrics")} yesNo /></td>
                      <td className="px-3 py-2.5"><ScopePill scope={u.floor_target_scope} /></td>
                      <td className="px-3 py-2.5"><ScopePill scope={u.incentive_scope} /></td>
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={() => openEdit(u)} data-testid={`overview-edit-${u.username}`} className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50"><PencilSimple size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white" data-testid="users-table">
          <div className="thin-scroll overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Username</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Salesman</th>
                  <th className="px-4 py-3">Floor</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.username}`}>
                    <td className="px-4 py-3 text-sm font-medium text-slate-800">{u.name}</td>
                    <td className="px-4 py-3 font-mono-data text-sm text-slate-600">@{u.username}</td>
                    <td className="px-4 py-3 text-sm capitalize text-slate-600">{u.role}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{u.salesman_name || "—"}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{u.current_floor || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                        {u.active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.username}`} className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50" title="Edit"><PencilSimple size={16} /></button>
                        <button onClick={() => setPwUser(u)} data-testid={`reset-pw-${u.username}`} className="rounded-md border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50" title="Reset password"><Key size={16} /></button>
                        {u.active ? (
                          <button onClick={() => setConfirmUser(u)} data-testid={`deactivate-user-${u.username}`} className="rounded-md border border-red-200 p-1.5 text-red-600 hover:bg-red-50" title="Deactivate user"><Prohibit size={16} /></button>
                        ) : (
                          <button onClick={() => activate(u)} data-testid={`activate-user-${u.username}`} className="rounded-md border border-emerald-200 p-1.5 text-emerald-600 hover:bg-emerald-50" title="Activate user"><CheckCircle size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {form && meta && (
        <Modal testId="user-modal" title={editing ? `Edit ${editing.username}` : "Add User"} onClose={() => setForm(null)}>
          <div className="space-y-3">
            {!editing && (
              <>
                <Field label="Username">
                  <input data-testid="form-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="inp" />
                </Field>
                <Field label="Password">
                  <input data-testid="form-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="inp" />
                </Field>
              </>
            )}
            <Field label="Full name">
              <input data-testid="form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" />
            </Field>
            <Field label="Role">
              <select data-testid="form-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="inp">
                {meta.roles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Salesman identity (for staff)">
              <input data-testid="form-salesman" value={form.salesman_name} onChange={(e) => setForm({ ...form, salesman_name: e.target.value })} className="inp" placeholder="e.g. Ahmed (exact POS name)" />
            </Field>
            <Field label="Current floor">
              <input list="floors-list" data-testid="form-floor" value={form.current_floor} onChange={(e) => setForm({ ...form, current_floor: e.target.value })} className="inp" placeholder="e.g. Ground Floor" />
              <datalist id="floors-list">{floors.map((f) => <option key={f} value={f} />)}</datalist>
            </Field>
            <Field label="Core floor (controls incentive doubling)">
              <input list="floors-list" data-testid="form-core-floor" value={form.core_floor} onChange={(e) => setForm({ ...form, core_floor: e.target.value })} className="inp" placeholder="e.g. Ground Floor" />
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="form-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4" /> Active
            </label>

            {(form.role === "staff" || form.role === "uploader") && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="dashboard-access-panel">
                <div className="mb-3 text-sm font-semibold text-slate-700">Dashboard Access</div>
                <div className="space-y-3">
                  {form.role === "uploader" && (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" data-testid="dash-view_store_metrics" checked={dashHas("view_store_metrics")} onChange={() => toggleDash("view_store_metrics")} className="h-4 w-4" />
                      View Store ABV / ABQ
                    </label>
                  )}
                  <div>
                    <div className="mb-1 text-xs font-medium text-slate-600">View Floor Target Achievement Of</div>
                    <FloorMultiSelect testId="scope-floor-target" value={form.floor_target_scope} floors={configFloors} onChange={(v) => setForm((f) => ({ ...f, floor_target_scope: v }))} />
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-medium text-slate-600">View Incentive Competition Of</div>
                    <FloorMultiSelect testId="scope-incentive" value={form.incentive_scope} floors={configFloors} onChange={(v) => setForm((f) => ({ ...f, incentive_scope: v }))} />
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-400">New {form.role} users have no floor access by default. Selecting "All Floors" auto-includes floors added later.</p>
              </div>
            )}

            <label className="flex items-center gap-2 border-t border-slate-100 pt-3 text-sm font-medium text-slate-700">
              <input type="checkbox" data-testid="form-custom-perms" checked={!!form.customPerms} onChange={(e) => setForm({ ...form, customPerms: e.target.checked, permissions: e.target.checked ? (editing?.permissions || meta.role_defaults[form.role] || []) : null })} className="h-4 w-4" />
              Customize permissions (override role defaults)
            </label>
            {form.customPerms && (
              <div className="grid grid-cols-1 gap-2 rounded-md bg-slate-50 p-3 sm:grid-cols-2">
                {meta.permissions.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" data-testid={`perm-${p}`} checked={(form.permissions || []).includes(p)} onChange={() => togglePerm(p)} className="h-4 w-4" />
                    {PERMISSION_LABELS[p] || p}
                  </label>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setForm(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="save-user-btn" className="rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </Modal>
      )}

      {pwUser && (
        <Modal testId="pw-modal" title={`Reset password · @${pwUser.username}`} onClose={() => { setPwUser(null); setPw(""); }}>
          <Field label="New password">
            <input data-testid="new-password-input" type="text" value={pw} onChange={(e) => setPw(e.target.value)} className="inp" />
          </Field>
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => { setPwUser(null); setPw(""); }} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={resetPassword} data-testid="save-password-btn" className="rounded-md bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800">Update</button>
          </div>
        </Modal>
      )}

      {confirmUser && (
        <Modal testId="deactivate-modal" title={`Deactivate @${confirmUser.username}?`} onClose={() => setConfirmUser(null)}>
          <p className="text-sm text-slate-600">
            The user will no longer be able to access the system, but historical records
            (sales, incentives, audit history) will be preserved. You can reactivate them anytime.
          </p>
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={() => setConfirmUser(null)} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={deactivate} disabled={statusBusy} data-testid="confirm-deactivate-btn" className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">{statusBusy ? "Deactivating…" : "Deactivate"}</button>
          </div>
        </Modal>
      )}

      <style>{`.inp{width:100%;border:1px solid #cbd5e1;border-radius:0.375rem;padding:0.5rem 0.75rem;font-size:0.875rem;outline:none}.inp:focus{border-color:#1d4ed8;box-shadow:0 0 0 2px #1d4ed8}`}</style>
    </Layout>
  );
}

function ScopePill({ scope, on, yesNo }) {
  if (yesNo) {
    return on
      ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">Yes</span>
      : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-400">No</span>;
  }
  if (scope === "all") return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">All Floors</span>;
  if (Array.isArray(scope) && scope.length) {
    return (
      <span className="flex flex-wrap gap-1">
        {scope.map((f) => <span key={f} className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">{f}</span>)}
      </span>
    );
  }
  return <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-400">None</span>;
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      {children}
    </div>
  );
}

// value: null/[] = no access, "all" = all configured (dynamic), or ["Floor A", ...]
function FloorMultiSelect({ value, floors, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const isAll = value === "all";
  const selected = isAll ? floors : (Array.isArray(value) ? value : []);
  const toggleFloor = (f) => {
    const base = isAll ? [...floors] : [...selected];
    const next = base.includes(f) ? base.filter((x) => x !== f) : [...base, f];
    onChange(next);
  };
  return (
    <div className="relative" data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}
        className="flex min-h-[38px] w-full flex-wrap items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-left text-sm hover:border-slate-400">
        {isAll ? (
          <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">All Floors</span>
        ) : selected.length === 0 ? (
          <span className="text-slate-400">No floors selected</span>
        ) : (
          selected.map((f) => (
            <span key={f} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
              {f}
              <X size={11} weight="bold" className="cursor-pointer hover:text-red-600" onClick={(e) => { e.stopPropagation(); onChange(selected.filter((x) => x !== f)); }} />
            </span>
          ))
        )}
        <span className="ml-auto text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          <div className="mb-1 flex gap-2 border-b border-slate-100 pb-1.5">
            <button type="button" onClick={() => onChange("all")} data-testid={`${testId}-all`} className="rounded px-2 py-0.5 text-xs font-semibold text-blue-700 hover:bg-blue-50">Select All</button>
            <button type="button" onClick={() => onChange([])} data-testid={`${testId}-clear`} className="rounded px-2 py-0.5 text-xs font-semibold text-slate-500 hover:bg-slate-100">Clear All</button>
          </div>
          <div className="max-h-44 space-y-1 overflow-y-auto">
            {floors.length === 0 && <div className="px-1 py-2 text-xs text-slate-400">No configured floors. Add floor targets in Settings.</div>}
            {floors.map((f) => (
              <label key={f} className="flex items-center gap-2 rounded px-1 py-1 text-sm text-slate-700 hover:bg-slate-50">
                <input type="checkbox" checked={isAll || selected.includes(f)} onChange={() => toggleFloor(f)} className="h-4 w-4" data-testid={`${testId}-opt-${f}`} />
                {f}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
